import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import CategoryHero from './components/CategoryHero';
import CategoryFilter from './components/CategoryFilter';
import SortAndView from './components/SortAndView';
import ProductGrid from './components/ProductGrid';
import ComparisonTable from './components/ComparisonTable';
import { useCart } from '../../context/CartContext';

import Button from '../../components/ui/Button';

const CategoryPages = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filters, setFilters] = useState({
    categories: [],
    brands: [],
    ratings: [],
    availability: [],
    priceRange: [0, 5000]
  });
  const [sortBy, setSortBy] = useState('popularity');
  const [viewMode, setViewMode] = useState('grid');
  const [wishlistItems, setWishlistItems] = useState([]);
  const [comparisonItems, setComparisonItems] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Mock Products Data
  const mockProducts = [
    {
      id: 1,
      name: "iPhone 15 Pro Max 256GB Natural Titanium",
      brand: "Apple",
      category: "Smartphones",
      price: 1199,
      originalPrice: 1299,
      discount: 8,
      rating: 4.8,
      reviewCount: 2847,
      image: "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=400",
      availability: "in-stock",
      isNew: true,
      isBestSeller: true,
      freeShipping: true,
      keyFeatures: ["A17 Pro Chip", "48MP Camera", "Titanium Design"],
      warranty: "1 Year Apple Care"
    },
    {
      id: 2,
      name: "MacBook Pro 14-inch M3 Pro 512GB Space Black",
      brand: "Apple",
      category: "Laptops",
      price: 2399,
      originalPrice: 2499,
      discount: 4,
      rating: 4.9,
      reviewCount: 1523,
      image: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=400",
      availability: "in-stock",
      isNew: true,
      freeShipping: true,
      keyFeatures: ["M3 Pro Chip", "14-inch Display", "512GB SSD"],
      warranty: "1 Year Limited"
    },
    {
      id: 3,
      name: "Samsung Galaxy S24 Ultra 512GB Titanium Gray",
      brand: "Samsung",
      category: "Smartphones",
      price: 1299,
      originalPrice: 1399,
      discount: 7,
      rating: 4.7,
      reviewCount: 1876,
      image: "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=400",
      availability: "in-stock",
      isBestSeller: true,
      freeShipping: true,
      keyFeatures: ["S Pen Included", "200MP Camera", "AI Features"],
      warranty: "1 Year Samsung Care"
    },
    {
      id: 4,
      name: "Dell XPS 13 Plus Intel i7 16GB RAM 1TB SSD",
      brand: "Dell",
      category: "Laptops",
      price: 1899,
      originalPrice: 2099,
      discount: 10,
      rating: 4.6,
      reviewCount: 987,
      image: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=400",
      availability: "low-stock",
      freeShipping: true,
      keyFeatures: ["Intel i7", "16GB RAM", "13.4-inch Display"],
      warranty: "2 Year Premium"
    },
    {
      id: 5,
      name: "AirPods Pro 2nd Generation with MagSafe Case",
      brand: "Apple",
      category: "AirPods & Earbuds",
      price: 249,
      originalPrice: 279,
      discount: 11,
      rating: 4.8,
      reviewCount: 3421,
      image: "https://images.unsplash.com/photo-1606220945770-b5b6c2c55bf1?w=400",
      availability: "in-stock",
      isBestSeller: true,
      freeShipping: true,
      keyFeatures: ["Active Noise Cancellation", "Spatial Audio", "MagSafe Case"],
      warranty: "1 Year Limited"
    },
    {
      id: 6,
      name: "Anker PowerCore 26800mAh Portable Charger",
      brand: "Anker",
      category: "Chargers",
      price: 89,
      originalPrice: 109,
      discount: 18,
      rating: 4.5,
      reviewCount: 5643,
      image: "https://images.unsplash.com/photo-1609592806787-3d8b9c2c6b8e?w=400",
      availability: "in-stock",
      freeShipping: true,
      keyFeatures: ["26800mAh Capacity", "Fast Charging", "3 USB Ports"],
      warranty: "18 Months"
    },
    {
      id: 7,
      name: "Sony WH-1000XM5 Wireless Noise Canceling Headphones",
      brand: "Sony",
      category: "Accessories",
      price: 399,
      originalPrice: 449,
      discount: 11,
      rating: 4.7,
      reviewCount: 2156,
      image: "https://images.unsplash.com/photo-1583394838336-acd977736f90?w=400",
      availability: "in-stock",
      freeShipping: true,
      keyFeatures: ["Industry Leading ANC", "30hr Battery", "Multipoint Connection"],
      warranty: "1 Year International"
    },
    {
      id: 8,
      name: "HP Spectre x360 14-inch 2-in-1 Laptop Intel Evo",
      brand: "HP",
      category: "Laptops",
      price: 1599,
      originalPrice: 1799,
      discount: 11,
      rating: 4.4,
      reviewCount: 743,
      image: "https://images.unsplash.com/photo-1541807084-5c52b6b3adef?w=400",
      availability: "pre-order",
      freeShipping: true,
      keyFeatures: ["2-in-1 Design", "Intel Evo Platform", "14-inch OLED"],
      warranty: "1 Year Limited"
    }
  ];

  // Filter and sort products
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...mockProducts];

    // Category filter
    if (selectedCategory !== 'all') {
      const categoryMap = {
        'phones': 'Smartphones',
        'laptops': 'Laptops',
        'chargers': 'Chargers',
        'accessories': 'Accessories',
        'airpods': 'AirPods & Earbuds'
      };
      filtered = filtered?.filter(product => product?.category === categoryMap?.[selectedCategory]);
    }

    // Apply filters
    if (filters?.categories?.length > 0) {
      const categoryMap = {
        'phones': 'Smartphones',
        'laptops': 'Laptops',
        'chargers': 'Chargers',
        'accessories': 'Accessories',
        'airpods': 'AirPods & Earbuds'
      };
      const selectedCategories = filters?.categories?.map(cat => categoryMap?.[cat]);
      filtered = filtered?.filter(product => selectedCategories?.includes(product?.category));
    }

    if (filters?.brands?.length > 0) {
      filtered = filtered?.filter(product => 
        filters?.brands?.some(brand => product?.brand?.toLowerCase() === brand)
      );
    }

    if (filters?.ratings?.length > 0) {
      filtered = filtered?.filter(product => {
        return filters?.ratings?.some(rating => {
          const minRating = parseInt(rating);
          return product?.rating >= minRating;
        });
      });
    }

    if (filters?.availability?.length > 0) {
      filtered = filtered?.filter(product => 
        filters?.availability?.includes(product?.availability)
      );
    }

    // Price range filter
    filtered = filtered?.filter(product => 
      product?.price >= filters?.priceRange?.[0] && product?.price <= filters?.priceRange?.[1]
    );

    // Sort products
    switch (sortBy) {
      case 'price-low':
        filtered?.sort((a, b) => a?.price - b?.price);
        break;
      case 'price-high':
        filtered?.sort((a, b) => b?.price - a?.price);
        break;
      case 'rating':
        filtered?.sort((a, b) => b?.rating - a?.rating);
        break;
      case 'newest':
        filtered?.sort((a, b) => b?.isNew - a?.isNew);
        break;
      case 'name-az':
        filtered?.sort((a, b) => a?.name?.localeCompare(b?.name));
        break;
      case 'name-za':
        filtered?.sort((a, b) => b?.name?.localeCompare(a?.name));
        break;
      default: // popularity
        filtered?.sort((a, b) => (b?.isBestSeller ? 1 : 0) - (a?.isBestSeller ? 1 : 0));
    }

    return filtered;
  }, [selectedCategory, filters, sortBy]);

  const comparisonProducts = mockProducts?.filter(product => 
    comparisonItems?.includes(product?.id)
  );

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      categories: [],
      brands: [],
      ratings: [],
      availability: [],
      priceRange: [0, 5000]
    });
    setSelectedCategory('all');
  };

  const handleAddToCart = (productId) => {
    const product = mockProducts.find(p => p.id === productId);
    if (product) {
      addToCart(product, 1);
      navigate('/shopping-cart');
    }
  };

  const handleAddToWishlist = (productId) => {
    setWishlistItems(prev => 
      prev?.includes(productId) 
        ? prev?.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleCompare = (productId) => {
    setComparisonItems(prev => {
      if (prev?.includes(productId)) {
        return prev?.filter(id => id !== productId);
      } else if (prev?.length < 4) {
        return [...prev, productId];
      } else {
        return prev;
      }
    });
  };

  const handleClearComparison = () => {
    setComparisonItems([]);
    setShowComparison(false);
  };

  const handleViewComparison = () => {
    if (comparisonItems?.length >= 2) {
      setShowComparison(true);
    }
  };

  const handleRemoveFromComparison = (productId) => {
    setComparisonItems(prev => prev?.filter(id => id !== productId));
    if (comparisonItems?.length <= 2) {
      setShowComparison(false);
    }
  };

  // Simulate loading
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, [selectedCategory, filters, sortBy]);

  return (
    <>
      <Helmet>
        <title>Category Pages - ElectroShop Pro | Premium Electronics Collection</title>
        <meta name="description" content="Browse our curated electronics collection with advanced filtering and comparison tools. Find the perfect smartphones, laptops, chargers, accessories, and AirPods." />
        <meta name="keywords" content="electronics, smartphones, laptops, chargers, accessories, airpods, tech products, online shopping" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          {/* Category Hero Section */}
          <section className="container mx-auto px-6 py-8">
            <CategoryHero
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              productCount={filteredAndSortedProducts?.length}
            />
          </section>

          {/* Main Content */}
          <section className="container mx-auto px-6 pb-16">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Sidebar Filters - Desktop */}
              <aside className="hidden lg:block w-80 flex-shrink-0">
                <div className="sticky top-24">
                  <CategoryFilter
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                  />
                </div>
              </aside>

              {/* Main Product Area */}
              <div className="flex-1 space-y-6">
                {/* Sort and View Controls */}
                <SortAndView
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  totalProducts={filteredAndSortedProducts?.length}
                  comparisonCount={comparisonItems?.length}
                  onClearComparison={handleClearComparison}
                  onViewComparison={handleViewComparison}
                />

                {/* Mobile Filter Toggle */}
                <div className="lg:hidden">
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={() => setShowMobileFilters(!showMobileFilters)}
                    iconName="Filter"
                    iconPosition="left"
                  >
                    {showMobileFilters ? 'Hide Filters' : 'Show Filters'}
                  </Button>
                </div>

                {/* Mobile Filters */}
                {showMobileFilters && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="lg:hidden"
                  >
                    <CategoryFilter
                      filters={filters}
                      onFilterChange={handleFilterChange}
                      onClearFilters={handleClearFilters}
                    />
                  </motion.div>
                )}

                {/* Product Grid */}
                <ProductGrid
                  products={filteredAndSortedProducts}
                  viewMode={viewMode}
                  sortBy={sortBy}
                  onAddToCart={handleAddToCart}
                  onAddToWishlist={handleAddToWishlist}
                  onCompare={handleCompare}
                  wishlistItems={wishlistItems}
                  comparisonItems={comparisonItems}
                  isLoading={isLoading}
                />
              </div>
            </div>
          </section>
        </main>

        {/* Comparison Table Modal */}
        {showComparison && (
          <ComparisonTable
            products={comparisonProducts}
            onRemoveProduct={handleRemoveFromComparison}
            onClose={() => setShowComparison(false)}
            onAddToCart={handleAddToCart}
          />
        )}

        {/* Floating Comparison Button */}
        {comparisonItems?.length > 0 && !showComparison && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-6 right-6 z-40"
          >
            <Button
              variant="default"
              onClick={handleViewComparison}
              disabled={comparisonItems?.length < 2}
              iconName="GitCompare"
              iconPosition="left"
              className="shadow-2xl trust-seal"
            >
              Compare ({comparisonItems?.length})
            </Button>
          </motion.div>
        )}
      </div>
    </>
  );
};

export default CategoryPages;